import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useAppDispatch } from "@/hooks/redux";
import { aiCreditsService } from "@/services/aiCreditsService";
import { setCreditBalance } from "@/store/slices/aiCreditsSlice";
import { captureFatalException } from "@/config/sentry";

// Page de succès après achat d'un pack de crédits IA. Calquée sur
// SubscriptionSuccessPage : la vérification synchrone (verify-session)
// crédite immédiatement si le webhook Stripe n'est pas encore passé —
// idempotent côté backend, donc sans risque de double crédit.
const CreditsSuccessPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"verifying" | "success" | "error">(
    "verifying",
  );
  const [balanceUsd, setBalanceUsd] = useState<number | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id") || "";

    if (!sessionId) {
      setStatus("error");
      return;
    }

    // sessionStorage pour survivre au double montage de React.StrictMode —
    // même pattern que SuccessPage. La vérification backend est idempotente,
    // le garde évite surtout un double appel réseau.
    const verificationKey = `stripe_credits_verified_${sessionId}`;
    if (sessionStorage.getItem(verificationKey)) {
      // Déjà vérifié dans cet onglet : re-lire simplement le solde.
      aiCreditsService
        .getBalance()
        .then((balance) => {
          dispatch(setCreditBalance(balance));
          setBalanceUsd(balance.balanceUsd);
          setStatus("success");
        })
        .catch(() => setStatus("success"));
      return;
    }
    sessionStorage.setItem(verificationKey, "true");

    (async () => {
      try {
        const { balance } = await aiCreditsService.verifyCreditSession(sessionId);
        dispatch(setCreditBalance(balance));
        setBalanceUsd(balance.balanceUsd);
        setStatus("success");
        toast.success(t("aiCredits.success.verified"));
      } catch (error) {
        captureFatalException(error as Error, {
          operation: "ai_credit_session_verification",
          sessionId,
          requires_immediate_attention: true,
          impact: "user_paid_but_credits_not_applied",
          financial_impact: true,
        });
        setStatus("error");
        toast.error(t("aiCredits.success.verificationError"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  // Parité d'affichage : 1 crédit-€ = 1 $ de budget interne (voir AiCreditsSection).
  const formattedBalance =
    balanceUsd != null
      ? new Intl.NumberFormat(locale, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 2,
        }).format(balanceUsd)
      : null;

  return (
    <div className="container mx-auto px-4 py-12 max-w-lg">
      <Card>
        <CardContent className="p-8 text-center space-y-4">
          {status === "verifying" && (
            <>
              <div
                className="mx-auto h-14 w-14 rounded-full bg-muted animate-pulse"
                aria-hidden="true"
              />
              <h1 className="text-lg font-semibold">
                {t("aiCredits.success.verifying")}
              </h1>
            </>
          )}

          {status === "success" && (
            <>
              <div
                className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40"
                aria-hidden="true"
              >
                <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h1 className="text-xl font-semibold tracking-tight">
                {t("aiCredits.success.title")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("aiCredits.success.description")}
              </p>
              {formattedBalance && (
                <div className="inline-flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-4 py-2">
                  <Coins
                    className="h-4 w-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="text-sm">
                    {t("aiCredits.success.newBalance")}{" "}
                    <strong className="tabular-nums">{formattedBalance}</strong>
                  </span>
                </div>
              )}
            </>
          )}

          {status === "error" && (
            <>
              <h1 className="text-xl font-semibold tracking-tight">
                {t("aiCredits.success.verificationErrorTitle")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("aiCredits.success.verificationErrorDesc")}
              </p>
            </>
          )}

          <Button
            onClick={() => navigate("/dashboard/subscription")}
            className="mt-2 gap-1.5 cursor-pointer transition-colors duration-150"
          >
            {t("aiCredits.success.backToSubscription")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditsSuccessPage;
